import { useDateFormat, useSearch, useTranslation } from '@eti/providers';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { date as dateUtil } from '@eti/utils';
import {
  User,
  CalendarBlank,
  ArrowRight as OneWayTripArrow,
  ArrowsLeftRight as ReturnTripArrow,
} from '@phosphor-icons/react';
import { breakpoints, Inline, LinkButton } from '@etg/wings';
import { TripType } from '@eti/schema-types';
import Media from 'react-media';

const titleStyles = css`
  flex-direction: column;
  font-size: 0.875rem;
  margin: auto 0;

  @media ${mediaQueries.medium.up} {
    align-items: center;
    flex-direction: row;
  }
`;

const titleTextStyles = css`
  color: ${etiColors.black};
  line-height: 1.25rem;
`;

const separatorStyles = css`
  display: inline-block;
  padding: 0 5px;
`;

const detailsStyles = css`
  color: ${etiColors.black};
  line-height: 1.25rem;
`;

const detailsItemStyles = css`
  align-items: center;
  display: flex;
  gap: 16px;

  @media ${mediaQueries.small.only} {
    flex-wrap: wrap;
  }
`;

const iconStyles = css`
  fill: #262626;
`;

const tripRoutesStyles = css`
  font-weight: 700;
`;

interface TripDetailsTitleProps {
  toggleTripDetailsDrawer: () => void;
}

const TripDetailsTitle = ({ toggleTripDetailsDrawer }: TripDetailsTitleProps) => {
  const { t } = useTranslation();
  const { currentSearchData } = useSearch();
  const { day } = useDateFormat();
  const { date, destination, origin, routes, travelers, tripType } = currentSearchData!;
  const TripTypeIcon = tripType === TripType.Return ? ReturnTripArrow : OneWayTripArrow;

  const getDestinationCode = () => {
    if (tripType === TripType.Return || tripType === TripType.OneWay) {
      return routes?.[0].destinationCode;
    }
    return routes?.[routes.length - 1].destinationCode;
  };

  return (
    <Inline
      alignY="center"
      className={detailsStyles}
      spacing={{
        [breakpoints._0]: 8,
        [breakpoints._768]: 16,
      }}
    >
      <div className={detailsItemStyles}>
        <Inline className={titleStyles} collapseBelow="768px" spacing={4}>
          <Inline className={titleTextStyles} data-testid="tripDetails-title-TitleText">
            <Inline alignY="center" className={tripRoutesStyles} role="paragraph" spacing={8}>
              <Media query={mediaQueries.small.only}>
                {(matches) =>
                  matches ? (
                    routes?.[0].originCode
                  ) : (
                    <span>
                      {origin?.city} ({routes?.[0].originCode})
                    </span>
                  )
                }
              </Media>
              <TripTypeIcon size={14} weight="fill" />
              <Media query={mediaQueries.small.only}>
                {(matches) =>
                  matches ? (
                    getDestinationCode()
                  ) : (
                    <span>
                      {destination?.city} ({getDestinationCode()})
                    </span>
                  )
                }
              </Media>
            </Inline>
          </Inline>
        </Inline>
        <Inline alignY="center" spacing={8}>
          <User className={iconStyles} id="adult-traveler" size={20} weight="light" />
          <div data-testid="tripDetails-title-travelers">{travelers?.summary}</div>
        </Inline>
      </div>
      <div className={detailsItemStyles}>
        <Inline alignY="center">
          <Inline alignY="center" spacing={8}>
            <CalendarBlank className={iconStyles} id="calendar" size={20} weight="light" />
            <Inline data-testid="tripDetails-title-date" spacing={0}>
              <time dateTime={date?.departure}>{dateUtil.format(date?.departure, day)}</time>
              {tripType !== TripType.OneWay && (
                <>
                  <span className={separatorStyles} data-testid="tripDetails-title-separator">
                    –
                  </span>
                  <time dateTime={date?.return}>{dateUtil.format(date?.return, day)}</time>
                </>
              )}
            </Inline>
          </Inline>
          <LinkButton data-testid="show-trip-detail-link" onClick={() => toggleTripDetailsDrawer()}>
            {t('YourSelectedTrip.Page.ShowTripDetail.Label')}
          </LinkButton>
        </Inline>
      </div>
    </Inline>
  );
};

export default TripDetailsTitle;
